import React, { createContext, useState } from 'react';
import PropTypes from 'prop-types';

const KitActivationFormContext = createContext();

const STATE_FORM_ERROR = `STATE_FORM_ERROR`;
const STATE_FORM_INVALID = `STATE_FORM_INVALID`;
const STATE_FORM_LOADING = `STATE_FORM_LOADING`;
const STATE_FORM_SUCCESS = `STATE_FORM_SUCCESS`;

const ACTIVATION_URL = `/drupal-api/kit/activate`;
const VALIDATION_URL = `/drupal-api/kit/check-id`;

const KitActivationFormContextProvider = ({ children }) => {
  const [kitActivationFormState, setKitActivationFormState] = useState(null);
  const [kitId, setKitId] = useState(null);
  const [kitValidationFormState, setKitValidationFormState] = useState(null);

  const [kitValidationFormErrors, setKitValidationFormErrors] = useState([]);

  const scrollToTop = () => {
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  };

  const activateKit = async (values) => {
    let response;
    const body = { ...values, kitId };

    setKitActivationFormState(STATE_FORM_LOADING);

    try {
      response = await fetch(ACTIVATION_URL, {
        method: `POST`,
        headers: {
          'Content-Type': `application/json`,
          Origin: `sequencing.com`,
        },
        body: JSON.stringify(body),
      });
    } catch (err) {
      setKitActivationFormState(STATE_FORM_ERROR);
      scrollToTop();
      return false;
    }

    if (!response || (response && (!response.ok || response.status !== 200))) {
      setKitActivationFormState(STATE_FORM_ERROR);
      return false;
    } else {
      const data = await response.json();
      if (
        data &&
        data.errors &&
        Array.isArray(data.errors) &&
        data.errors.find((item) => !!item.error)
      ) {
        setKitActivationFormState(STATE_FORM_INVALID);
        return data.errors;
      }
    }

    setKitId(kitId);
    setKitActivationFormState(STATE_FORM_SUCCESS);
    scrollToTop();
    return body;
  };

  const validateKitId = async (kitId) => {
    let response;

    setKitId(kitId);
    setKitValidationFormState(STATE_FORM_LOADING);

    try {
      response = await fetch(VALIDATION_URL, {
        method: `POST`,
        headers: {
          'Content-Type': `application/json`,
          Origin: `sequencing.com`,
        },
        body: JSON.stringify({ kitId }),
      });
    } catch (err) {
      console.log(`Something unexpected happened`);
      setKitValidationFormState(STATE_FORM_ERROR);
      return false;
    }

    if (!response || (response && (!response.ok || response.status !== 200))) {
      setKitValidationFormState(STATE_FORM_ERROR);
      return false;
    } else {
      const data = await response.json();
      if (
        data &&
        data.errors &&
        Array.isArray(data.errors) &&
        data.errors.find(
          (item) =>
            item.error &&
            item.path &&
            item.path.length &&
            item.path[0] === `kitId`
        )
      ) {
        setKitValidationFormErrors(data.errors);
        setKitValidationFormState(STATE_FORM_INVALID);
        return false;
      }
    }

    setKitActivationFormState(null);
    setKitValidationFormState(STATE_FORM_SUCCESS);
    return kitId;
  };

  const resetState = () => {
    setKitActivationFormState(null);
    setKitId(null);
    setKitValidationFormState(null);
  };

  return (
    <KitActivationFormContext.Provider
      value={{
        activateKit,
        kitActivationFormState,
        kitId,
        kitValidationFormErrors,
        kitValidationFormState,
        resetState,
        validateKitId,
      }}
    >
      {children}
    </KitActivationFormContext.Provider>
  );
};

KitActivationFormContextProvider.propTypes = {
  children: PropTypes.node,
};

export { KitActivationFormContextProvider };
